import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import GlobalCursor from '../components/GlobalCursor'
import FullscreenViewer from '../components/FullscreenViewer'
import VideoListModal from '../components/VideoListModal'
import { useProjects } from '../hooks/useProjects'

const dedupeProjects = (projects) => {
  const seen = new Set()
  return projects.filter((project) => {
    const key = project.id || project.title
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export default function Projects() {
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [currentProject, setCurrentProject] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0)
  const [showInfo, setShowInfo] = useState(false)
  const [isIdle, setIsIdle] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const [showSingleImage, setShowSingleImage] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const [isVideoListOpen, setIsVideoListOpen] = useState(false)
  const [videoListProject, setVideoListProject] = useState(null)
  const [reopenVideoListOnClose, setReopenVideoListOnClose] = useState(false)

  const videoRef = useRef(null)
  const videoContainerRef = useRef(null)
  const idleTimeoutRef = useRef(null)

const { projects, loading, error } = useProjects()

  const projectTypeFilters = ['all', 'video-editing', 'graphic-design', 'website']


  const mapCategoryToType = (category) => {
    const normalized = (category || '').toLowerCase()
    if (normalized === 'video-editing') return 'video-editing'
    if (normalized === 'graphic-design') return 'graphic-design'
    if (normalized === 'web-development' || normalized === 'website' || normalized === 'web') return 'website'
    return 'website'
  }

  // Video player functions
  const handleVideoPlay = () => {
    setIsVideoPlaying(true)
    resetIdleTimer()
  }

  const handleVideoPause = () => {
    setIsVideoPlaying(false)
    resetIdleTimer()
  }

  const handleVideoEnded = () => {
    setIsVideoPlaying(false)
    resetIdleTimer()
  }

  const handleTimeUpdate = () => {
    if (videoRef.current && !isDragging) {
      const current = videoRef.current.currentTime
      const total = videoRef.current.duration
      setCurrentTime(current)
      setProgress((current / total) * 100)
    }
  }

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration)
    }
  }

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
    }
  }

  const toggleMute = () => {
    setIsMuted(!isMuted)
    if (videoRef.current) {
      videoRef.current.muted = !isMuted
    }
  }

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume)
    setIsMuted(newVolume === 0)
    if (videoRef.current) {
      videoRef.current.volume = newVolume
      videoRef.current.muted = newVolume === 0
    }
  }

  const toggleVolumeSlider = () => {
    setShowVolumeSlider(!showVolumeSlider)
  }

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const resetIdleTimer = () => {
    setIsIdle(false)
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current)
    }
    idleTimeoutRef.current = setTimeout(() => {
      setIsIdle(true)
    }, 3500)
  }
  // Ensure initial video audio settings are applied when opening the player
  useEffect(() => {
    if (isFullscreen && videoRef.current) {
      videoRef.current.muted = isMuted
      videoRef.current.volume = volume
    }
  }, [isFullscreen, isMuted, volume])


  // Mouse movement handlers for idle timer
  useEffect(() => {
    let idleTimer

    const handleMouseMove = () => {
      resetIdleTimer()
    }

    const handleMouseLeave = () => {
      // Don't hide controls when mouse leaves the window
      clearTimeout(idleTimer)
    }

    // Start the timer when video player opens
    if (isFullscreen) {
      resetIdleTimer()
    }

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseleave', handleMouseLeave)

    // Cleanup
    return () => {
      clearTimeout(idleTimer)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isFullscreen, currentProject])

  // Keyboard shortcuts for player usability
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!isFullscreen) return
      // Keep controls visible while using keyboard
      resetIdleTimer()
      // Avoid interfering with inputs
      const tag = (e.target && e.target.tagName) || ''
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return

      if (e.code === 'Space' || e.key === 'k' || e.key === 'K') {
        e.preventDefault()
        togglePlayPause()
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault()
        toggleMute()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleVolumeChange(Math.min(1, parseFloat((volume + 0.05).toFixed(2))))
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleVolumeChange(Math.max(0, parseFloat((volume - 0.05).toFixed(2))))
      } else if (e.key === 'ArrowRight' || e.key === 'l' || e.key === 'L') {
        e.preventDefault()
        if (videoRef.current) {
          const newTime = Math.min(videoRef.current.duration || 0, (videoRef.current.currentTime || 0) + 5)
          videoRef.current.currentTime = newTime
          setCurrentTime(newTime)
          setProgress((newTime / (videoRef.current.duration || 1)) * 100)
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'j' || e.key === 'J') {
        e.preventDefault()
        if (videoRef.current) {
          const newTime = Math.max(0, (videoRef.current.currentTime || 0) - 5)
          videoRef.current.currentTime = newTime
          setCurrentTime(newTime)
          setProgress((newTime / (videoRef.current.duration || 1)) * 100)
        }
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isFullscreen, volume, togglePlayPause, toggleMute])

  const handleSeekBarMouseDown = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
    updateSeekPosition(e.clientX)
  }

  const updateSeekPosition = (clientX) => {
    if (videoContainerRef.current && videoRef.current) {
      const rect = videoContainerRef.current.getBoundingClientRect()
      const clickX = clientX - rect.left
      const newProgress = (clickX / rect.width) * 100
      const clampedProgress = Math.max(0, Math.min(100, newProgress))

      setProgress(clampedProgress)
      const newTime = (clampedProgress / 100) * videoRef.current.duration
      videoRef.current.currentTime = newTime
      setCurrentTime(newTime)
    }
  }

  // Image gallery functions
  const viewSingleImage = (index) => {
    setCurrentImageIndex(index)
    setIsImageLoading(true)
    setShowSingleImage(true)
  }

  const navigateImage = (direction) => {
    const galleryImages = currentProject?.galleryImages || []
    let newIndex = currentImageIndex
    if (direction === 'next') {
      newIndex = (currentImageIndex + 1) % galleryImages.length
    } else {
      newIndex = currentImageIndex === 0 ? galleryImages.length - 1 : currentImageIndex - 1
    }
    setCurrentImageIndex(newIndex)
    setIsImageLoading(true)
  }

  const openFullscreenWithProject = (project) => {
    setCurrentProject(project)
    setIsFullscreen(true)
    setCurrentProjectIndex(filteredProjects.findIndex(p => p.id === project.id))
    setShowInfo(false)
    setIsIdle(false)
    resetIdleTimer()
  }

  const handleProjectClick = (project) => {
    const isVideoProject = mapCategoryToType(project.category) === 'video-editing'
    if (isVideoProject) {
      const hasVideoList = Array.isArray(project.videos) && project.videos.length > 0
      const fallbackUrl = project.projectUrl || project.videoFile?.url || ''
      const derivedVideos = hasVideoList
        ? project.videos
        : (fallbackUrl
          ? [{
              title: project.title || 'Video',
              url: fallbackUrl,
              thumbnail: project.heroImage?.url,
              duration: project.duration,
            }]
          : [])

      const projectForModal = { ...project, videos: derivedVideos }
      setVideoListProject(projectForModal)
      setIsVideoListOpen(true)
      return
    }
    openFullscreenWithProject(project)
  }

  const closeFullscreen = () => {
    setIsFullscreen(false)
    setCurrentProject(null)
    setShowInfo(false)
    setShowSingleImage(false)
    setCurrentImageIndex(0)
    setIsIdle(false)
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current)
    }
    // If a video was launched from the list, restore the modal
    if (reopenVideoListOnClose && videoListProject?.videos?.length > 0) {
      setIsVideoListOpen(true)
    }
    setReopenVideoListOnClose(false)
  }

  const navigateToProject = (direction) => {
    if (isNavigating) return
    setIsNavigating(true)

    let newIndex = currentProjectIndex
    if (direction === 'next') {
      newIndex = (currentProjectIndex + 1) % filteredProjects.length
    } else {
      newIndex = currentProjectIndex === 0 ? filteredProjects.length - 1 : currentProjectIndex - 1
    }

    setCurrentProjectIndex(newIndex)
    setCurrentProject(filteredProjects[newIndex])

    setTimeout(() => setIsNavigating(false), 300)
  }

  const projectsToUse = dedupeProjects(projects?.projectsList || [])

  const projectsWithTypes = projectsToUse.map((project) => ({
    ...project,
    projectType: mapCategoryToType(project.category),
    // Transform images array to galleryImages array (array of URLs)
    galleryImages: project.images?.map(img => img.url) || project.galleryImages || [],
  }))

  const filteredProjects =
    selectedFilter === 'all'
      ? projectsWithTypes
      : projectsWithTypes.filter((project) => project.projectType === selectedFilter)

  // Global mouse event handlers for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        updateSeekPosition(e.clientX)
      }
    }

    const handleGlobalMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove)
      document.addEventListener('mouseup', handleGlobalMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isDragging])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      <GlobalCursor />
      <div className="absolute inset-0 stars"></div>
      <div className="absolute inset-0 stars2"></div>

      <div className="relative z-20 px-4 sm:px-6 py-6 sm:py-8 max-w-6xl mx-auto pt-24 sm:pt-32 md:pt-40 pointer-events-none">
        {/* Page Header */}
        <div className="mb-8 pointer-events-auto">
          <motion.h1
            className="text-7xl sm:text-8xl md:text-9xl font-bold text-white mb-4 text-center font-monument-extended"
            style={{
              lineHeight: '0.7',
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              ease: 'easeOut',
            }}
          >
            explore
            <br />
            creations
          </motion.h1>
        </div>

        {/* Loading and Error States */}
        {loading && (
          <div className="text-center py-12 pointer-events-auto">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E06100]"></div>
            <p className="text-white mt-4">Loading projects...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-12 pointer-events-auto">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-[#E06100] text-white rounded-full hover:bg-[#C55000] transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Filter Card */}
        <div className="bg-black border border-gray-800 rounded-2xl p-4 shadow-2xl mb-8 pointer-events-auto">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center">
            {projectTypeFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 capitalize ${
                  selectedFilter === filter
                    ? 'bg-[#E06100] text-white'
                    : 'text-white hover:text-[#E06100]'
                }`}
              >
                {filter === 'all'
                  ? 'All'
                  : filter === 'website'
                    ? 'Web Development'
                    : filter === 'video-editing'
                      ? 'Video Editing'
                      : filter === 'graphic-design'
                        ? 'Graphic Design'
                        : filter.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 pointer-events-auto">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id || `${project.title}-${index}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.5,
                delay: index * 0.1,
              }}
              whileHover={{ y: -5 }}
              className="group cursor-pointer hover:cursor-pointer interactive-element project-card"
              style={{ cursor: 'pointer' }}
              data-cursor="hover"
              data-interactive="true"
              role="button"
              tabIndex={0}
              onClick={() => handleProjectClick(project)}
            >
              <div className="relative overflow-hidden rounded-2xl bg-black/20 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer glass-card">
                {/* Image Section - Top */}
                <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden rounded-t-2xl bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-sm cursor-pointer">
                  <img
                    src={project?.heroImage?.url || ''}
                    alt={project?.title || ''}
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

                  {/* Play Button Overlay for Video Projects */}
                  {project?.category === 'video-editing' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center group-hover:bg-white/30 transition-all duration-300">
                        <svg
                          className="w-8 h-8 text-white ml-1"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Category Badge */}
                  <div className="absolute top-3 right-3">
                    <span className="inline-block px-3 py-1.5 bg-black/70 backdrop-blur-md text-white text-xs font-medium rounded-full border border-white/20">
                      {mapCategoryToType(project.category) === 'website'
                        ? 'Website'
                        : mapCategoryToType(project.category) === 'graphic-design'
                          ? 'Graphic Design'
                          : project.category === 'video-editing'
                            ? 'Video Editing'
                            : project.category === 'photography'
                              ? 'Photography'
                              : project.category}
                    </span>
                  </div>

                  {/* Duration Badge for Video */}
                  {project?.category === 'video-editing' && (
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-block px-2 py-1 bg-black/70 backdrop-blur-md text-white text-xs font-medium rounded-full border border-white/20">
                        {project.duration || 'N/A'}
                      </span>
                    </div>
                  )}

                  {/* Poster Count Badge for Graphic Design */}
                  {project?.category === 'graphic-design' && (
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-block px-2 py-1 bg-black/70 backdrop-blur-md text-white text-xs font-medium rounded-full border border-white/20 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {(() => {
                          const galleryCount = project.galleryImages?.length || 0
                          return galleryCount > 0 ? `${galleryCount}` : '1'
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Status Badge for Website Projects */}
                  {mapCategoryToType(project.category) === 'website' && (
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-block px-2 py-1 bg-green-900/70 backdrop-blur-md text-green-400 text-xs font-medium rounded-full border border-green-500/20">
                        Live
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="relative p-3 sm:p-4 md:p-6 flex flex-col cursor-pointer bg-white/5 backdrop-blur-sm">
                  {/* Client */}
                  <p className="text-[#E06100] text-sm font-medium mb-1">{project.client}</p>

                  {/* Project Title */}
                  <h3 className="text-lg sm:text-xl font-bold text-white leading-tight group-hover:text-gray-100 transition-colors mb-2">
                    {project?.title || 'Untitled Project'}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-300 text-sm leading-relaxed mb-2 line-clamp-2">
                    {project?.description || 'No description available'}
                  </p>

                  {/* Shine Effect */}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Fullscreen Project Viewer */}
      <FullscreenViewer
        isFullscreen={isFullscreen}
        currentProject={currentProject}
        showInfo={showInfo}
        setShowInfo={setShowInfo}
        closeFullscreen={closeFullscreen}
        mapCategoryToType={mapCategoryToType}
        // Video player props
        videoRef={videoRef}
        videoContainerRef={videoContainerRef}
        isIdle={isIdle}
        isVideoPlaying={isVideoPlaying}
        isMuted={isMuted}
        volume={volume}
        currentTime={currentTime}
        duration={duration}
        progress={progress}
        showVolumeSlider={showVolumeSlider}
        isDragging={isDragging}
        handleVideoPlay={handleVideoPlay}
        handleVideoPause={handleVideoPause}
        handleVideoEnded={handleVideoEnded}
        handleTimeUpdate={handleTimeUpdate}
        handleLoadedMetadata={handleLoadedMetadata}
        togglePlayPause={togglePlayPause}
        toggleMute={toggleMute}
        handleVolumeChange={handleVolumeChange}
        toggleVolumeSlider={toggleVolumeSlider}
        formatTime={formatTime}
        handleSeekBarMouseDown={handleSeekBarMouseDown}
        // Image gallery props
        showSingleImage={showSingleImage}
        currentImageIndex={currentImageIndex}
        viewSingleImage={viewSingleImage}
        navigateImage={navigateImage}
        setIsImageLoading={setIsImageLoading}
        setShowSingleImage={setShowSingleImage}
        setCurrentImageIndex={setCurrentImageIndex}
        // Navigation props
        navigateToProject={navigateToProject}
        isNavigating={isNavigating}
        currentProjectIndex={currentProjectIndex}
        filteredProjects={filteredProjects}
      />

      {/* Video List Modal for Video Projects */}
      <VideoListModal
        isOpen={isVideoListOpen}
        project={videoListProject}
        onClose={() => {
          setIsVideoListOpen(false)
          setVideoListProject(null)
          setReopenVideoListOnClose(false)
        }}
        onSelectVideo={(video) => {
          if (!video) return
          const selectedUrl = video.url || video.src || video.link || ''
          if (!selectedUrl) return
          const baseProject = videoListProject || currentProject
          const projectWithSelectedVideo = {
            ...baseProject,
            projectUrl: selectedUrl,
          }
          setIsVideoListOpen(false)
          // Remember to reopen the list when user exits the player
          setReopenVideoListOnClose(true)
          openFullscreenWithProject(projectWithSelectedVideo)
        }}
      />
    </div>
  )
}